import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AioDrawerComponent, AioEmptyStateComponent } from '@aio/ui';
import { environment } from '../../../../environments/environment';
import { getTenantIdFromRouter } from '../../../core/utils/tenant-route.util';
import { MessageComposer } from '../components/message-composer';
import { MessageList } from '../components/message-list';
import { SlotFiller, SlotSubmission } from '../components/slot-filler';
import { AgentApiService } from '../core/agent-api.service';
import { AgentAuthService } from '../core/agent-auth.service';
import { pickDefaultModel } from '../core/agent-model.util';
import {
  buildPrompt,
  displayPrompt,
  leafNeedsUserInput,
  withTenantContext,
} from '../core/agent-prompt.util';
import { AgentSignalRService } from '../core/agent-signalr.service';
import {
  AgentEvent,
  AgentEventTypes,
  QueryNodeDto,
  UiArtifact,
  UiMessage,
} from '../core/agent.models';

/** Matches the full AI Chat page (`/:tenantId/ai-chat`), where the buddy stays out of the way. */
const FULL_CHAT_ROUTE = /^\/[^/]+\/ai-chat(?:[/?#]|$)/;

/**
 * Floating "chat buddy": a bottom-right launcher available on every authenticated screen that
 * opens a lightweight AI chat in a right-hand drawer. It reuses the AI chat API/SignalR services
 * and message components; the full-featured experience (history, artifacts, guided questions)
 * stays on the AI Chat page, which is one click away from the drawer header.
 */
@Component({
  selector: 'app-chat-buddy',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AioDrawerComponent, AioEmptyStateComponent, MessageList, MessageComposer, SlotFiller],
  templateUrl: './chat-buddy.component.html',
  styleUrl: './chat-buddy.component.scss',
})
export class ChatBuddyComponent implements OnInit {
  private readonly api = inject(AgentApiService);
  private readonly agentAuth = inject(AgentAuthService);
  protected readonly signalr = inject(AgentSignalRService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Drawer visibility. */
  readonly open = signal(false);
  /** True while the full AI Chat page is the active route (launcher hidden). */
  readonly onFullChatPage = signal(false);
  private readonly tenantId = signal('');
  readonly fullChatLink = computed(() => {
    const tenantId = this.tenantId();
    return tenantId ? `/${tenantId}/ai-chat` : null;
  });

  readonly messages = signal<UiMessage[]>([]);
  readonly conversationId = signal<number | null>(null);
  readonly busy = signal(false);
  readonly runError = signal<string | null>(null);
  /** Transient "what the agent is doing now" line shown as a spinner row while the loop runs. */
  readonly activeStatus = signal<string | null>(null);
  readonly downloadingArtifactId = signal<number | null>(null);
  /** A reply finished while the drawer was closed; shows a dot on the launcher. */
  readonly hasUnread = signal(false);

  private readonly selectedModelKey = signal('');
  readonly canSend = computed(() => this.selectedModelKey().length > 0);

  // ---- Guided prompting (same taxonomy as the AI Chat page) ----
  readonly taxonomy = signal<QueryNodeDto[]>([]);
  readonly navParentId = signal<number | null>(null);
  readonly navPath = signal<{ id: number; label: string }[]>([]);
  readonly primaryServerId = signal<number | null>(null);
  readonly activeLeaf = signal<QueryNodeDto | null>(null);
  readonly suggestionsCollapsed = signal(false);

  readonly currentSuggestions = computed(() => {
    const parent = this.navParentId();
    return this.taxonomy()
      .filter((n) => (n.parentId ?? null) === parent)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  });

  readonly navBreadcrumb = computed(() => this.navPath().map((p) => p.label).join(' › '));

  /** Developer/debug view: render raw per-tool cards instead of only the status spinner. */
  readonly showToolDetails = environment.showAgentToolDetails;

  private readonly threadRef = viewChild<ElementRef<HTMLElement>>('threadEl');

  private catalogRequested = false;
  /** Client-side ids for live (not-yet-persisted) bubbles; negative so they never clash with DB ids. */
  private tempIdSeq = 0;

  ngOnInit(): void {
    this.syncRoute(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.syncRoute(event.urlAfterRedirects));

    this.signalr.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((evt) => this.handleEvent(evt));
  }

  private syncRoute(url: string): void {
    const onPage = FULL_CHAT_ROUTE.test(url);
    this.onFullChatPage.set(onPage);
    this.tenantId.set(getTenantIdFromRouter(this.router));
    if (!onPage) {
      return;
    }
    // The full page owns the shared hub stream while it is mounted: close the drawer and stop
    // any in-flight buddy run so both views never render the same events.
    if (this.open()) {
      this.open.set(false);
    }
    if (this.busy()) {
      void this.signalr.cancelRun();
      this.finishStreaming();
      this.hasUnread.set(false);
      this.runError.set('Stopped because the full AI Chat page was opened.');
    }
  }

  toggleDrawer(): void {
    if (this.open()) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  openDrawer(): void {
    this.open.set(true);
    this.hasUnread.set(false);
    void this.ensureReady();
    this.scheduleScrollToBottom();
  }

  closeDrawer(): void {
    this.open.set(false);
  }

  /**
   * Lazily loads the model catalog, the guided-question taxonomy and opens the hub connection
   * the first time the drawer opens.
   */
  private async ensureReady(): Promise<void> {
    if (!this.catalogRequested) {
      this.catalogRequested = true;
      this.api.getModels().subscribe({
        next: (models) => {
          const preferred = pickDefaultModel(models);
          if (preferred) {
            this.selectedModelKey.set(preferred.key);
          }
        },
        error: () => {
          this.catalogRequested = false;
          this.runError.set('Unable to load the AI assistant. Please try again.');
        },
      });

      // One MCP server per prompt: the first one drives the suggested-question taxonomy.
      this.api.getMcpServers().subscribe((servers) => {
        const primary = servers[0];
        if (!primary) {
          return;
        }
        this.primaryServerId.set(primary.id);
        this.api.getQueryTaxonomy(primary.id).subscribe((nodes) => {
          this.taxonomy.set(nodes);
          this.resetNav();
        });
      });
    }

    try {
      await this.signalr.connect();
    } catch (err) {
      this.runError.set(
        err instanceof Error ? err.message : 'Unable to connect to the AI assistant.',
      );
    }
  }

  newChat(): void {
    if (this.busy()) {
      this.cancel();
    }
    this.conversationId.set(null);
    this.messages.set([]);
    this.runError.set(null);
    this.activeStatus.set(null);
    this.activeLeaf.set(null);
    this.resetNav();
  }

  // ---- Guided prompting ----

  sessionTenantId(): string {
    return this.agentAuth.sessionTenantId();
  }

  pickNode(node: QueryNodeDto): void {
    if (node.nodeType === 'leaf') {
      if (leafNeedsUserInput(node)) {
        this.activeLeaf.set(node);
        return;
      }
      const template = node.promptTemplate?.trim() || node.label;
      const prompt =
        (node.slots?.length ?? 0) > 0 ? buildPrompt(template, this.sessionTenantValues()) : template;
      this.resetNav();
      this.send(prompt, { guided: true });
      return;
    }

    this.navPath.update((path) => [...path, { id: node.id, label: node.label }]);
    this.navParentId.set(node.id);
  }

  navBack(): void {
    this.navPath.update((path) => path.slice(0, -1));
    const path = this.navPath();
    this.navParentId.set(path.length ? path[path.length - 1].id : null);
  }

  toggleSuggestions(): void {
    this.suggestionsCollapsed.update((v) => !v);
  }

  closeSlotFiller(): void {
    this.activeLeaf.set(null);
  }

  onSlotsSubmit(submission: SlotSubmission): void {
    const leaf = this.activeLeaf();
    if (!leaf) {
      return;
    }
    const template = leaf.promptTemplate?.trim() || leaf.label;
    const values = { ...submission.values, ...this.sessionTenantValues() };
    const prompt = buildPrompt(template, values, submission.labels);
    this.activeLeaf.set(null);
    this.resetNav();
    this.send(prompt, { guided: true });
  }

  private resetNav(): void {
    this.navParentId.set(null);
    this.navPath.set([]);
  }

  /** Session tenant substituted into `{tenant}` so the user never types a tenant id. */
  private sessionTenantValues(): Record<string, string> {
    const tenantId = this.sessionTenantId();
    return tenantId ? { tenant: tenantId } : {};
  }

  openFullChat(): void {
    const link = this.fullChatLink();
    if (!link) {
      return;
    }
    this.closeDrawer();
    void this.router.navigateByUrl(link);
  }

  /**
   * Sends a prompt. Guided prompts already carry the tenant via `{tenant}`; free-text prompts
   * get the signed-in tenant appended as a hidden context note so the agent never asks for it.
   * The bubble always shows only the user-facing wording.
   */
  send(text: string, options: { guided?: boolean } = {}): void {
    if (!this.canSend()) {
      return;
    }

    const prompt = options.guided ? text : withTenantContext(text, this.sessionTenantId());

    this.runError.set(null);
    this.messages.update((list) => [
      ...list,
      {
        id: this.nextTempId(),
        sequenceNo: 0,
        role: 'user',
        text: displayPrompt(prompt),
        toolCalls: [],
        streaming: false,
      },
    ]);
    this.scheduleScrollToBottom();
    this.activeStatus.set('Working…');
    this.busy.set(true);

    void this.signalr.sendPrompt({
      conversationId: this.conversationId(),
      prompt,
      modelKey: this.selectedModelKey(),
      // App is implicit from the token; the backend enables all tools bound to that app.
      mcpServerIds: [],
    });
  }

  cancel(): void {
    void this.signalr.cancelRun();
    this.finishStreaming();
  }

  /** Fetches a persisted artifact's bytes through the API and triggers a browser save. */
  downloadArtifact(artifact: UiArtifact): void {
    if (artifact.kind !== 'file' || artifact.artifactId == null) {
      return;
    }
    this.downloadingArtifactId.set(artifact.artifactId);
    this.api.downloadConversationArtifact(artifact.artifactId).subscribe({
      next: (result) => {
        this.downloadingArtifactId.set(null);
        const anchor = document.createElement('a');
        if (result instanceof Blob) {
          const url = URL.createObjectURL(result);
          anchor.href = url;
          anchor.download = artifact.fileName || 'artifact';
          anchor.click();
          URL.revokeObjectURL(url);
        } else {
          anchor.href = result.sasUrl;
          anchor.click();
        }
      },
      error: () => {
        this.downloadingArtifactId.set(null);
        this.runError.set('Failed to download the artifact.');
      },
    });
  }

  private handleEvent(evt: AgentEvent): void {
    // The hub stream is shared with the AI Chat page. Only react to events for a run this
    // drawer started (busy), and never to another conversation's events.
    if (!this.busy()) {
      return;
    }
    const current = this.conversationId();
    if (evt.conversationId != null && current != null && evt.conversationId !== current) {
      return;
    }

    const stick = this.isNearBottom();
    switch (evt.type) {
      case AgentEventTypes.ConversationStarted:
        if (evt.conversationId != null && current == null) {
          this.conversationId.set(evt.conversationId);
        }
        break;

      case AgentEventTypes.AssistantDelta:
        this.appendAssistantText(evt.text ?? '');
        break;

      case AgentEventTypes.AssistantMessage:
        this.setAssistantTextIfEmpty(evt.text ?? '');
        break;

      case AgentEventTypes.AgentStatus:
        this.closeOpenAssistant();
        this.activeStatus.set(evt.text || null);
        break;

      case AgentEventTypes.ToolStart:
        this.addToolCall(evt);
        break;

      case AgentEventTypes.ToolResult:
        this.updateToolCall(evt);
        break;

      case AgentEventTypes.ArtifactFile:
        if (evt.url) {
          const chip: UiArtifact = {
            kind: 'file',
            url: evt.url,
            fileName: evt.fileName ?? undefined,
            mimeType: evt.mimeType ?? undefined,
          };
          this.ensureOpenAssistant();
          this.mutateOpenAssistant((m) => ({ ...m, artifacts: [...(m.artifacts ?? []), chip] }));
        }
        break;

      case AgentEventTypes.Error: {
        const message = evt.error?.trim() || 'Something went wrong.';
        this.runError.set(message);
        this.ensureOpenAssistant();
        this.mutateOpenAssistant((m) => ({ ...m, error: message }));
        this.finishStreaming();
        break;
      }

      case AgentEventTypes.Done:
        this.finishStreaming();
        break;
    }

    if (stick) {
      this.scheduleScrollToBottom();
    }
  }

  private ensureOpenAssistant(): void {
    this.messages.update((list) => {
      const last = list[list.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        return list;
      }
      return [
        ...list,
        { id: this.nextTempId(), sequenceNo: 0, role: 'assistant', text: '', toolCalls: [], streaming: true },
      ];
    });
  }

  private mutateOpenAssistant(fn: (msg: UiMessage) => UiMessage): void {
    this.messages.update((list) => {
      if (list.length === 0) {
        return list;
      }
      const lastIndex = list.length - 1;
      const last = list[lastIndex];
      if (last.role !== 'assistant' || !last.streaming) {
        return list;
      }
      const next = [...list];
      next[lastIndex] = fn(last);
      return next;
    });
  }

  private closeOpenAssistant(): void {
    this.mutateOpenAssistant((m) => ({ ...m, streaming: false }));
  }

  private appendAssistantText(text: string): void {
    this.activeStatus.set(null);
    this.ensureOpenAssistant();
    this.mutateOpenAssistant((m) => ({ ...m, text: m.text + text }));
  }

  private setAssistantTextIfEmpty(text: string): void {
    this.activeStatus.set(null);
    this.ensureOpenAssistant();
    this.mutateOpenAssistant((m) => (m.text ? m : { ...m, text }));
  }

  private addToolCall(evt: AgentEvent): void {
    this.ensureOpenAssistant();
    this.mutateOpenAssistant((m) => ({
      ...m,
      toolCalls: [
        ...m.toolCalls,
        {
          id: evt.toolCallId ?? crypto.randomUUID(),
          name: evt.toolName ?? 'tool',
          serverKey: evt.serverKey ?? undefined,
          argumentsJson: evt.argumentsJson ?? undefined,
          status: 'pending',
        },
      ],
    }));
  }

  private updateToolCall(evt: AgentEvent): void {
    this.messages.update((list) =>
      list.map((m) => ({
        ...m,
        toolCalls: m.toolCalls.map((tc) =>
          tc.id === evt.toolCallId
            ? { ...tc, resultJson: evt.resultJson ?? undefined, status: evt.isError ? 'error' : 'success' }
            : tc,
        ),
      })),
    );
  }

  private finishStreaming(): void {
    this.busy.set(false);
    this.activeStatus.set(null);
    this.closeOpenAssistant();
    if (!this.open()) {
      this.hasUnread.set(true);
    }
  }

  private nextTempId(): number {
    return --this.tempIdSeq;
  }

  private scheduleScrollToBottom(): void {
    requestAnimationFrame(() => {
      const el = this.threadRef()?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }

  private isNearBottom(threshold = 120): boolean {
    const el = this.threadRef()?.nativeElement;
    if (!el) {
      return true;
    }
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  }
}
